import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { notification } from "antd"
import { getSession, postLogin, postLogout, TSession } from "../../auth/sessionApi"
import { resetSessionExpired, setAccountIncompleteHandler, setUnauthorizedHandler, TUnauthorizedReason } from "../../auth/AuthenticatedAxios"
import { loginPathFor } from "../../auth/redirect"
import { messageInfo } from "../../services/messages"
import { clearSessionStorageByPrefix } from "../../services/functions"
import { ModalContext } from "./ModalContext"
import { SidePanelContext } from "./SidePanelContext"

export type AuthContextProps = {
    isAuthenticated: boolean
    isLoading: boolean
    /** angemeldet, aber kein Mitarbeiter-Datensatz verknüpft (403 "Unkown user") */
    accountIncomplete: boolean
    session?: TSession
    login: (email: string, password: string) => Promise<void>
    logout: (reason?: TUnauthorizedReason) => Promise<void>
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined)

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) throw new Error("useAuth must be used within AuthContextProvider")
    return context
}

export const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [session, setSession] = useState<TSession | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(true)
    const [accountIncomplete, setAccountIncomplete] = useState(false)
    const isLoggingOutRef = useRef(false)
    const sessionRef = useRef<TSession | undefined>(undefined)
    sessionRef.current = session
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const modalContext = useContext(ModalContext)
    const sidePanelContext = useContext(SidePanelContext)
    // Kontexte über Refs, damit logout stabil bleibt (die Kontextwerte ändern sich bei jedem Öffnen/Schließen)
    const uiRef = useRef({ modalContext, sidePanelContext })
    uiRef.current = { modalContext, sidePanelContext }

    // beim Start: bestehende Session prüfen (200 -> angemeldet, 401 -> Login-Seite)
    useEffect(() => {
        let cancelled = false
        getSession()
            .then((data) => !cancelled && setSession(data))
            .catch(() => !cancelled && setSession(undefined))
            .finally(() => !cancelled && setIsLoading(false))
        return () => {
            cancelled = true
        }
    }, [])

    const login = useCallback(
        async (email: string, password: string) => {
            const data = await postLogin(email, password)
            // keine Daten eines vorherigen Benutzers weiterverwenden
            queryClient.clear()
            resetSessionExpired()
            setAccountIncomplete(false)
            setSession(data)
        },
        [queryClient]
    )

    const logout = useCallback(
        async (reason?: TUnauthorizedReason) => {
            // schon abgemeldet (z. B. verspätete 401 einer alten Anfrage) -> nichts tun
            if (isLoggingOutRef.current || !sessionRef.current) return
            isLoggingOutRef.current = true
            try {
                // Aufräumen bei jedem Logout (manuell und Sitzungsablauf)
                clearSessionStorageByPrefix()
                uiRef.current.modalContext?.onScreenChangeCloseAllModals()
                uiRef.current.sidePanelContext?.onScreenChangeCloseAllSidePanels()
                notification.destroy()
                if (reason === "session-expired") {
                    messageInfo("session-expired", "Sitzung abgelaufen", "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.", 6)
                    // zurück auf die aktuelle Seite nach erneuter Anmeldung
                    navigate(loginPathFor(window.location.pathname, window.location.search, window.location.hash), { replace: true })
                } else {
                    navigate("/login", { replace: true })
                }
                setSession(undefined)
                setAccountIncomplete(false)
                queryClient.clear()
                if (reason !== "session-expired") {
                    // Cookie serverseitig löschen; Fehler ignorieren (Session evtl. schon ungültig)
                    await postLogout().catch(() => undefined)
                }
            } finally {
                isLoggingOutRef.current = false
            }
        },
        [navigate, queryClient]
    )

    useEffect(() => {
        setUnauthorizedHandler((reason) => void logout(reason))
        setAccountIncompleteHandler(() => sessionRef.current && setAccountIncomplete(true))
        return () => {
            setUnauthorizedHandler(null)
            setAccountIncompleteHandler(null)
        }
    }, [logout])

    const value = useMemo(() => ({ isAuthenticated: !!session, isLoading, accountIncomplete, session, login, logout }), [session, isLoading, accountIncomplete, login, logout])

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
